<template lang="html">
    <div v-if="article">
        <!-- title section start -->
        <section>
            <div class="row" style="padding: 0px 10px;">

                <h2 class="abstract-heading" v-if="!hideTitle">Title and Abstract</h2>
                <div class="col-md-12 form-group">
                    <label for="fname">Title<span class="required"></span></label>
                    <input
                        type="text"
                        class="form-control"
                        v-on:keyup="checkTitle()"
                        v-on:change="onChange()"
                        v-model="article.title"
                        required="required"
                    />
                    <div style="width: 100%" v-if="errors && errors['title']">
                        <span v-for="error in errors['title']" style="color: red;" v-html="error"></span>
                    </div>
                    <div style="width: 100%" v-if="titleWarning">
                        <p style="margin: 0px; color: #e09202">
                            WARNING: Please read our <a target="_blank" href="https://support.jmir.org/hc/en-us/articles/115002943791">title guidelines</a> and make sure to add a colon followed by the <a href="https://support.jmir.org/hc/en-us/articles/360032676471?flash_digest=adb2e0dc7d3a5ccefe02dd4f06ce545db43fbbaa">method/design</a> to the title, if appropriate.
                        </p>
                    </div>
                </div>

            </div>
        </section>
        <!-- title section end-->

        <!-- abstract section start -->
        <section>
            <label v-bind:class="{ 'asterik': !article || !article.section || !article.section.abstracts_disabled }">Abstract</label>

            <div class="author-metadata mt-0">
                <div class="row content-padding abstract" ref="abstract">

                    <form class="col-md-12 no-border">

                        <template v-if="!article.section || (article.section && article.section.abstracts_disabled == 0)">
                            <label>
                                <input
                                    type="radio"
                                    id="structuredAbstract"
                                    name="abstractType"
                                    value="structured"
                                    v-on:change="onChange()"
                                    v-model="abstractType"
                                    @click="changeAbstract('structured')"/>
                                <b>Structured (Preferred)</b>
                                <span class="radio"></span>
                                <p>Use for original paper, systematic review or consensus paper.
                                    <b>Maximum 450 words</b>
                                </p>
                            </label>

                            <label>
                                <input
                                    type="radio"
                                    id="unstructuredAbstract"
                                    name="abstractType"
                                    value="unstructured"
                                    v-on:change="onChange()"
                                    v-model="abstractType"
                                    @click="changeAbstract('unstructured')"/>
                                <b>Unstructured</b>
                                <span class="radio"></span>
                                <p>Use for proposals, comments, tutorials, reviews, etc.
                                    <b>Maximum {{ unstructuredWordCount }} words</b>
                                </p>
                            </label>

                            <div>
                                <div class="form-group mt-5">
                                    <div v-if="extraText">
                                        <label>
                                            Extra text
                                        </label>

                                        <textarea
                                            class="form-control"
                                            style="height: 150px"
                                            id="extraText"
                                            v-on:change="onChange()"
                                            v-model="extraText">
                                        </textarea>
                                    </div>

                                    <div v-for="(text, section) of article.abstract_fields">
                                        <label
                                        v-bind:class="{
                                            'asterik': section != 'Trial Registration'
                                        }">
                                            {{ section | ucfirst }}
                                        </label>

                                        <textarea
                                            class="form-control"
                                            style="height: 150px"
                                            v-bind:style="{
                                                'borderColor': (errors && errors[section] && errors[section].length > 0) ? 'red' : '#b7b7b7'
                                            }"
                                            v-on:keyup="abstractUpdated(section)"
                                            v-bind:id="section"
                                            v-on:change="onChange()"
                                            v-model="article.abstract_fields[section]">
                                        </textarea>
                                        <div style="width: 100%">
                                            <template v-if="errors && errors[section] && errors[section].length > 0">
                                                <span v-for="error in errors[section]" style="color: red; width: 100%; display: inline-block">{{ error }}</span>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                                <span style="text-align: right; margin: 0px; width: 100%; float: right; font-size: 13px">
                                    Total word count: {{ getWordCount() }} / {{ (article.abstract_fields && Object.keys(article.abstract_fields).length == 1 ? unstructuredWordCount : structuredWordCount) }}
                                </span>
                            </div>

                        </template>

                        <template v-else>
                            <p>Abstracts are unavailable in this section.</p>
                        </template>
                    </form>
                </div>
            </div>
        </section>

        <div class="form-group">
            <label for="keywords">
                Keywords
            </label>
            <p>
                Choose terms that best describe the submission's content
                following the categories used by this journal, and examples
                provided by the editor. Please add up to 10 keywords.

                <br>
                <br>

                Separate terms with a semi-colon (term1; term2; term3).
            </p>

            <input
                type="text"
                id="keywords"
                class="form-control"
                placeholder="Keywords"
                v-bind:style="{
                    'borderColor': (errors && errors[section] && errors[section].length > 0) ? 'red' : '#b7b7b7'
                }"
                v-model="article.subject_class"
            />

            <p v-if="errors && errors['keywords']" style="color: red">{{ errors['keywords'][0] }}</p>
        </div>

        <ai-usage-declaration
            v-if="showAiUsage"
            ref="aiUsage"
            v-bind:article-id="article.article_id"
            v-bind:edit-decision-id="editDecisionId"
            v-bind:change-event="changeEvent"
            v-on:loaded="onAiUsageLoaded"
            v-on:completion-change="onAiUsageCompletionChange">
        </ai-usage-declaration>

        <button
            type="button"
            class="save-button"
            v-bind:class="{
                'deactive': (saving || invalidTransferSelections || (saveVersion && (!isFileUploaded || !hasEditorResponse)) || (showAiUsage && !aiUsageComplete))
            }"
            style="display: block; margin: 1rem 0.25rem 0px 0px;"
            v-bind:style="{
                'float': (hideTitle ? (cancelEvent ? 'left' : 'none') : 'right')
            }"
            v-bind:disabled="saving"
            v-on:click="saveMetadata()">
            <span v-if="saving" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
            {{
                saving ?
                'Saving...' :
                    (
                        saveText ?
                            saveText :
                            'Save non-author metadata'
                    )
            }}
        </button>

        <button
            type="button"
            id="cancelSavingNonAuthor"
            class="secondary-button"
            style="display: inline-block; margin: 1rem 0px 0px 0.25px;"
            v-on:click="cancel()"
            v-if="cancelEvent != false">
            Cancel
        </button>

        <br />

        <div v-if="errors && Object.keys(errors).length > 0 && Object.keys(errors).filter(key => { return errors[key].length > 0 }).length > 0">
            <p style="margin: 0px">
                Some errors have occured:
            </p>
            <ul>
                <li v-for="errorSection in Object.keys(errors).filter(key => { return errors[key].length > 0 })">
                    <p style="color: red" v-for="error in errors[errorSection].filter(error => { return error != '' })" v-html="error"></p>
                </li>
            </ul>
        </div>

        <div v-if="titleWarning">
            <p style="margin: 0px; color: #e09202">
                WARNING: Please read our <a target="_blank" href="https://support.jmir.org/hc/en-us/articles/115002943791">title guidelines</a> and make sure to add a colon followed by the <a href="https://support.jmir.org/hc/en-us/articles/360032676471?flash_digest=adb2e0dc7d3a5ccefe02dd4f06ce545db43fbbaa">method/design</a> to the title, if appropriate.
            </p>
        </div>
    </div>
</template>

<script>
export default {
    props: [
        "reload",
        "article",
        "saveText",
        "hideTitle",
        "noDefault",
        "saveEvent",
        "saveVersion",
        "cancelEvent",
        "changeEvent",
        "isFileUploaded",
        "hasEditorResponse",
        "showAiUsage",
        "editDecisionId"
    ],
    filters: {
        ucfirst: value => {
            if (value) {
                return value[0].toUpperCase() + value.substring(1);
            }
        }
    },
    data() {
        return {
            invalidTransferSelections: false,
            abstracts: [
                "Background",
                "Objective",
                "Methods",
                "Results",
                "Conclusions",
                "Trial Registration"
            ],
            dragging: false,
            extraText: null,
            errors: {},
            titleWarning: false,
            saving: false,
            initalLoad: true,
            abstractType: null,
            structuredWordCount: 450,
            unstructuredWordCount: 500,
            keyword: null,
            // Only read when showAiUsage is true; starts false so the Submit button stays
            // disabled until the AI usage question has actually been answered.
            aiUsageComplete: false
        };
    },
    methods: {
        abstractUpdated(section) {
            let sectionText = this.article.abstract_fields[section].trim();
            let message = "";
            let index = -1;

            if (!this.errors[section]) {
                this.errors[section] = [];
            }

            if (sectionText == "" || sectionText.length == 1) {
                index = this.errors[section].indexOf(
                    "Please refrain from using all lower case."
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }

                index = this.errors[section].indexOf(
                    "Please refrain from using all upper case."
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }
            } else if (
                sectionText != "" &&
                sectionText.toUpperCase() == sectionText
            ) {
                message = "Please refrain from using all upper case.";

                index = this.errors[section].indexOf(
                    "Please refrain from using all lower case."
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }

                index = this.errors[section].indexOf(
                    `${section} is a required field and cannot be left empty.`
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }
            } else if (
                sectionText != "" &&
                sectionText.toLowerCase() == sectionText
            ) {
                message = "Please refrain from using all lower case.";

                index = this.errors[section].indexOf(
                    "Please refrain from using all upper case."
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }

                index = this.errors[section].indexOf(
                    `${section} is a required field and cannot be left empty.`
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }
            } else {
                index = this.errors[section].indexOf(
                    "Please refrain from using all lower case."
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }

                index = this.errors[section].indexOf(
                    "Please refrain from using all upper case."
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }

                index = this.errors[section].indexOf(
                    `${section} is a required field and cannot be left empty.`
                );

                if (index != -1) {
                    this.errors[section].splice(index, 1);
                }
            }

            if (message && this.errors[section].indexOf(message) == -1) {
                this.errors[section].push(message);
            }

            this.$forceUpdate();
        },
        onChange() {
            if (window.top[this.changeEvent]) {
                window.top[this.changeEvent]();
            }
        },
        cancel() {
            if (window.top[this.cancelEvent] && !this.saving) {
                window.top[this.cancelEvent]();
            }
        },
        changeAbstract(abstractType) {
            if (abstractType == this.abstractType) return;

            let oldType = this.abstractType;

            this.abstractType = abstractType;

            if (oldType == "unstructured" && abstractType == "structured") {
                this.errors["unstructured"] = [];

                let indexes = [];
                let result = undefined;
                let extraText = true;

                for (let abstract of this.abstracts) {
                    result = this.article.abstract_fields.unstructured.match(
                        new RegExp(`${abstract}s?:`, "i")
                    );
                    if (result) {
                        if (result["index"] == 0) {
                            extraText = false;
                        }
                        indexes.push({
                            offset: result[0].length + 1,
                            index: result["index"],
                            abstract: abstract
                        });
                    } else {
                        indexes.push({
                            offset: 0,
                            index: -1,
                            abstract: abstract
                        });
                    }
                }

                indexes.sort((a, b) => {
                    return b.index - a.index;
                });

                let previousIndex = -1;
                let abstractObjects = {};

                for (let index of indexes) {
                    let content = "";
                    if (index.index != -1) {
                        if (previousIndex != -1) {
                            content = this.article.abstract_fields.unstructured.substring(
                                index.index + index.offset,
                                previousIndex
                            );
                        } else {
                            content = this.article.abstract_fields.unstructured.substring(
                                index.index + index.offset
                            );
                        }
                        previousIndex = index.index;
                    }
                    abstractObjects[index.abstract] = content;
                }

                if (extraText) {
                    let content = "";

                    if (previousIndex != -1) {
                        content = this.article.abstract_fields.unstructured.substring(
                            0,
                            previousIndex
                        );
                    } else {
                        content = this.article.abstract_fields.unstructured.substring(
                            0
                        );
                    }

                    this.extraText = content;
                }

                delete abstractObjects["unstructured"];

                this.fixAbstracts(abstractObjects);
            } else {
                let abstract = "";

                if (this.extraText) {
                    abstract += this.extraText + " ";
                    this.extraText = null;
                }

                for (let key in this.article.abstract_fields) {
                    this.errors[key] = [];
                    if (this.article.abstract_fields[key]) {
                        abstract +=
                            key +
                            ": " +
                            this.article.abstract_fields[key] +
                            " ";
                    }
                }

                abstract.trim();
                this.article.abstract_fields = {
                    unstructured: abstract
                };
            }

            this.$forceUpdate();

            setTimeout(() => {
                updateIFrameHeight();
            }, 250);
        },
        fixAbstracts(abstractObjects, first = false) {
            let abstractObjectsNew = {};

            for (let abstract of this.abstracts) {
                if (first && this.noDefault) {
                    abstractObjectsNew[abstract] = "";
                } else if (abstractObjects[abstract]) {
                    abstractObjectsNew[abstract] = abstractObjects[abstract];
                } else {
                    abstractObjectsNew[abstract] = "";
                }
            }

            this.article.abstract_fields = abstractObjectsNew;
        },
        getWordCount() {
            let count = 0;

            for (let abstract in this.article.abstract_fields) {
                let temp = this.article.abstract_fields[abstract]
                    .trim()
                    .split(" ");

                if (temp.length > 1 || (temp.length == 1 && temp[0] != "")) {
                    count += temp.length;
                }
            }

            return count;
        },
        checkTitle() {
            if (this.article.title == "") {
                return;
            } else if (
                this.errors &&
                this.errors["title"] &&
                this.errors["title"].length > 0
            ) {
                this.errors["title"] = [];
            }

            if (this.article.title.indexOf(":") == -1) {
                this.titleWarning = true;
            } else {
                this.titleWarning = false;
            }

            setTimeout(() => {
                updateIFrameHeight();
            }, 250);
        },
        onAiUsageLoaded() {
            this.$nextTick(() => {
                updateIFrameHeight();
            });
        },
        onAiUsageCompletionChange(isComplete) {
            this.aiUsageComplete = isComplete;
        },
        async saveMetadata() {
            this.checkTitle();

            if (this.showAiUsage && this.$refs.aiUsage && !this.$refs.aiUsage.validate()) {
                setTimeout(() => {
                    updateIFrameHeight();
                }, 250);
                return;
            }

            let result = null;
            this.saving = true;

            this.article.abstractType = this.abstractType;

            if (this.showAiUsage && this.$refs.aiUsage) {
                Object.assign(this.article, this.$refs.aiUsage.payload);
            }

            await axios
                .post(
                    `/api/articles/${this.article.article_id}?saveVersion=${this.saveVersion}`,
                    this.article
                )
                .then(response => {
                    if (!response.data.status) {
                        this.errors = response.data.errors;

                        setTimeout(() => {
                            updateIFrameHeight();
                        }, 250);

                        this.saving = false;
                        return;
                    } else {
                        this.errors = [];
                    }

                    if (this.reload) {
                        window.top.location.reload();
                    } else if (this.saveEvent && window.top[this.saveEvent]) {
                        window.top[this.saveEvent]();
                        
                        this.saving = false;
                    }

                    for (let frameIndex in Object.keys(window.parent.frames)) {
                        let frame = window.parent.frames[frameIndex] 

                        if (frame) {
                            frame.dispatchEvent(
                                new Event('jmir-save')
                            )
                        }
                    }
                })
                .catch(error => {
                    let errors = error.response.data;
                    
                    if (!errors.length) {
                        // We had an error with something other than invalid data.
                    }
                    
                    this.errors = errors;
                    this.saving = false;
                });
        }
    },
    updated() {
        if (this.article && this.article.abstract_fields && this.initalLoad) {
            if (Object.keys(this.article.abstract_fields).length > 1) {
                this.abstractType = "structured";
                this.fixAbstracts(this.article.abstract_fields);
            } else if (Object.keys(this.article.abstract_fields).length == 1) {
                this.abstractType = "unstructured";
            }

            this.initalLoad = false;
            setTimeout(() => {
                this.$forceUpdate();
            }, 250);
        }

        updateIFrameHeight();
    },
    mounted() {
        let self = this
        if (window) {
            window.parent.addEventListener('jmir-validation', function(evt) {
                console.log(evt.detail)
                if (evt.detail == true) {
                    self.invalidTransferSelections = false
                } else {
                    self.invalidTransferSelections = true
                }
            })
        }
    },
    created() {
        if (this.article && this.article.abstract_fields) {
            if (this.noDefault) {
                this.article.title = "";
                this.article.keywords = [];
            }

            if (this.article.abstract_fields["registered-report"]) {
                delete this.article.abstract_fields["registered-report"];
            }

            if (
                Object.keys(this.article.abstract_fields).length > 2 ||
                this.noDefault
            ) {
                this.fixAbstracts(this.article.abstract_fields, true);
                this.abstractType = "structured";
            } else if (Object.keys(this.article.abstract_fields).length <= 2) {
                this.abstractType = "unstructured";
            }

            this.$forceUpdate();
        }

        updateIFrameHeight();
    }
};
</script>

<style lang="css" scoped>
.required::after {
    content: "*";
    color: red;
}
input.invalid {
    border: 1px solid red !important;
    background-color: #fff2f1 !important;
}
.keywords {
    padding-left: 0px;
    padding-right: 0px;
    list-style: none;
    display: flex;
    flex-wrap: wrap;
}
.keyword-pill {
    padding: 5px;
    margin: 2px;
    background-color: #e3e3e3;
    border: none;
    border-radius: 20px;
}
</style>
